import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";
import { getFromCache, setToCache } from "./cacheService";
import { updateStoreStock } from "./storesService";

export const updateTournamentPoints = async (entryId: number, points: number) => {
    try {
        const { error } = await supabase
          .from('tournament_entries')
          .update({ points, updated_at: new Date().toISOString() })
          .eq('id', entryId);

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error updating tournament points:', error);
        return false;
    }
};

export const updateTournamentEntryPosition = async (entryId: number | string, finalPosition: number | string | null) => {
    try {
        const val = finalPosition !== null && finalPosition !== undefined && String(finalPosition).trim() !== ''
            ? parseInt(String(finalPosition), 10)
            : null;

        if (val !== null && (isNaN(val) || val < 1)) {
            return { error: 'A posição final deve ser um número maior ou igual a 1.' };
        }

        const { error } = await supabase
            .from('tournament_entries')
            .update({ 
                final_position: val,
                updated_at: new Date().toISOString()
            })
            .eq('id', entryId);

        if (error) throw error;
        return { success: true };
    } catch (error: any) {
        console.error('Error updating tournament entry position:', error);
        return { error: error?.message || 'Erro ao atualizar a posição do participante.' };
    }
};


export const getRankings = async (limit: number = 10) => {
  try {
    const { data: topCollectors, error: collectorsError } = await supabase
      .rpc('get_top_user_cards_with_profile', { p_limit: limit });
    
    const { data: topTraders, error: tradersError } = await supabase
      .rpc('get_top_offerlist_users_with_profile', { p_limit: limit });

    if (collectorsError) {
        console.error('Error in get_top_user_cards_with_profile:', collectorsError);
    }
    if (tradersError) {
        console.error('Error in get_top_offerlist_users_with_profile:', tradersError);
    }

    return {
      topCollectors: (topCollectors || []).map((u: any) => ({
          ...u,
          username: u.username || `user_${u.user_id}`, // Fallback if username is null
          total_cards: Number(u.total_cards || 0)
      })),
      topTraders: (topTraders || []).map((u: any) => ({
          ...u,
          username: u.username || `user_${u.user_id}`,
          total_cards: Number(u.total_cards || 0)
      }))
    };
  } catch (error) {
    console.error('Error fetching rankings:', error);
    return { topCollectors: [], topTraders: [] };
  }
};


export const getAllTournaments = async () => {
  try {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        games:games!tournaments_cardgame_id_fkey(id, name),
        game_formats:game_formats!tournaments_format_id_fkey(id, name),
        tournament_types:tournament_types!tournaments_tournament_type_id_fkey(id, name),
        tournament_event_categories:tournament_event_categories!tournaments_event_category_id_fkey(id, name),
        game_sets:game_sets!tournaments_tourney_set_id_fkey(id, name, code),
        store:stores(
          id,
          name,
          slug,
          logo
        ),
        creator:users!tournaments_created_by_fkey(
          id, 
          username, 
          avatar,
          store_members(
            store:stores(
              id,
              name,
              slug,
              logo
            )
          )
        )
      `)
      .order('starts_at', { ascending: true });

    if (error) throw error;
    
    // Map starts_at to start_date for frontend compatibility
    const mapped = (data || []).map((t: any) => {
      return {
        ...t,
        game_formats: t.game_formats || t.tournament_formats,
        tournament_formats: t.game_formats || t.tournament_formats,
        tickets: [],
        start_date: t.starts_at || t.start_date
      };
    });

    return mapped;
  } catch (error: any) {
    console.error('Error fetching tournaments with joins, attempting fallback:', error);
    try {
      const { data: fallbackData, error: fbError } = await supabase
        .from('tournaments')
        .select('*')
        .order('starts_at', { ascending: true });
      if (fbError) throw fbError;
      return (fallbackData || []).map((t: any) => ({
        ...t,
        start_date: t.starts_at || t.start_date
      }));
    } catch (fbErr) {
      console.error('Error fetching tournaments fallback:', fbErr);
      return [];
    }
  }
};


export const getGameFormats = async (gameId?: number | string) => {
  const cacheKey = gameId && gameId !== 'All' ? `game_formats_${gameId}` : 'game_formats_all';
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    let query = supabase
      .from('game_formats')
      .select('id, name, description, slug, game_id')
      .order('name', { ascending: true });

    if (gameId && gameId !== 'All') {
      const numGameId = Number(gameId);
      if (!isNaN(numGameId)) {
        query = query.or(`game_id.eq.${numGameId},game_id.is.null`);
      }
    }

    const { data, error } = await query;
    if (error) throw error;
    setToCache(cacheKey, data || []);
    return data || [];
  } catch (error) {
    console.error('Error fetching game formats, trying fallback:', error);
    try {
      const { data: legacyData } = await supabase
        .from('tournament_formats')
        .select('*')
        .order('name', { ascending: true });
      if (legacyData && legacyData.length > 0) {
        return legacyData;
      }
    } catch {}
    return [];
  }
};

export const getTournamentFormats = getGameFormats;


export const getMyTournaments = async (storeId?: number | string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return [];

    let resolvedStoreId = storeId ? Number(storeId) : null;
    if (!resolvedStoreId && profile.store_id) {
      resolvedStoreId = Number(profile.store_id);
    }
    if (!resolvedStoreId) {
      const { data: memberStore } = await supabase
        .from('store_members')
        .select('store_id')
        .eq('user_id', profile.id)
        .maybeSingle();
      if (memberStore) resolvedStoreId = Number(memberStore.store_id);
    }

    let query = supabase
      .from('tournaments')
      .select(`
        *,
        games:games!tournaments_cardgame_id_fkey(id, name),
        game_formats:game_formats!tournaments_format_id_fkey(id, name),
        tournament_types:tournament_types!tournaments_tournament_type_id_fkey(id, name),
        tournament_event_categories:tournament_event_categories!tournaments_event_category_id_fkey(id, name),
        game_sets:game_sets!tournaments_tourney_set_id_fkey(id, name, code),
        store:stores(
          id,
          name,
          slug,
          logo
        )
      `);

    if (resolvedStoreId) {
      query = query.or(`created_by.eq.${profile.id},store_id.eq.${resolvedStoreId}`);
    } else {
      query = query.eq('created_by', profile.id);
    }

    const { data, error } = await query.order('starts_at', { ascending: false });

    if (error) throw error;
    
    // Map starts_at to start_date for compatibility and link format fields
    const mapped = (data || []).map((t: any) => {
      return {
        ...t,
        tickets: [],
        game_formats: t.game_formats || t.tournament_formats,
        tournament_formats: t.game_formats || t.tournament_formats,
        start_date: t.starts_at || t.start_date
      };
    });

    return mapped;
  } catch (error) {
    console.error('Error fetching my tournaments with joins, trying fallback:', error);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];
      const profile = await getUserByAuthId(session.user.id);
      if (!profile) return [];

      const { data: fbData, error: fbErr } = await supabase
        .from('tournaments')
        .select('*')
        .eq('created_by', profile.id)
        .order('starts_at', { ascending: false });
      if (fbErr) throw fbErr;
      return (fbData || []).map((t: any) => ({
        ...t,
        start_date: t.starts_at || t.start_date
      }));
    } catch (finalErr) {
      console.error('Error in getMyTournaments final fallback:', finalErr);
      return [];
    }
  }
};


export const createTournament = async (tournamentData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Sessão expirada');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('Perfil não encontrado');

    // Find store ID owned by this user via store_members to associate the tournament and product
    const { data: store } = await supabase
      .from('stores')
      .select('id, store_members!inner(user_id)')
      .eq('store_members.user_id', profile.id)
      .maybeSingle();

    // Helper to generate an 8-character unique alphanumeric ticket code
    const generateTicketCode = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let res = '';
      for (let i = 0; i < 8; i++) {
        res += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return res;
    };

    const cleanTicket = tournamentData.ticket && typeof tournamentData.ticket === 'string' && tournamentData.ticket.trim().length > 0
      ? tournamentData.ticket.trim().toUpperCase().substring(0, 8)
      : generateTicketCode();

    // 1. Create Tournament with all new columns from public.tournaments
    const payload: any = {
      name: tournamentData.name?.trim(),
      cardgame_id: tournamentData.cardgame_id ? Number(tournamentData.cardgame_id) : null,
      format_id: tournamentData.format_id ? Number(tournamentData.format_id) : null,
      tournament_type_id: tournamentData.tournament_type_id ? Number(tournamentData.tournament_type_id) : null,
      event_category_id: tournamentData.event_category_id ? Number(tournamentData.event_category_id) : null,
      tourney_set_id: tournamentData.tourney_set_id ? Number(tournamentData.tourney_set_id) : null,
      starts_at: tournamentData.starts_at || tournamentData.start_date || null,
      ends_at: tournamentData.ends_at || null,
      max_players: Number(tournamentData.max_players) || 8,
      online: Boolean(tournamentData.online),
      decklist_required: Boolean(tournamentData.decklist_required),
      ticket: cleanTicket,
      current_round: Number(tournamentData.current_round) || 0,
      created_by: profile.id,
      store_id: tournamentData.store_id ? Number(tournamentData.store_id) : (store?.id || null),
      status: tournamentData.status || 'open'
    };

    const { data: tournament, error: tError } = await supabase
      .from('tournaments')
      .insert(payload)
      .select()
      .maybeSingle();

    if (tError) throw tError;

    // 2. Create Ticket Product & add to store stock (required for all tournaments)
    let ticketResult: any = null;
    let ticketError: string | null = null;

    if (tournament) {
      try {
        const tournamentCapacity = Math.max(1, Number(tournament.max_players) || 8);
        const ticketRes = await createTournamentTicketProduct(
          tournament,
          {
            ticket_price: Number(tournamentData.ticket_price) || 0,
            ticket_quantity: tournamentCapacity,
            max_quantity: tournamentCapacity,
            max_per_user: Math.max(1, Number(tournamentData.max_per_user) || 1),
            is_active: tournamentData.is_active !== undefined ? Boolean(tournamentData.is_active) : true,
            image_url: tournamentData.image_url || null,
            description: tournamentData.description || null
          },
          tournament.store_id || store?.id || null,
          profile.id
        );

        if (ticketRes && (ticketRes as any).error) {
          console.error('Error creating ticket product for tournament:', (ticketRes as any).error);
          ticketError = (ticketRes as any).error;
        } else {
          ticketResult = ticketRes;
        }
      } catch (err: any) {
        console.error('Exception creating ticket product for tournament:', err);
        ticketError = err?.message || 'Erro ao criar ingresso';
      }
    }

    return {
      ...tournament,
      ticket_result: ticketResult,
      ticket_error: ticketError
    };
  } catch (error: any) {
    console.error('Error creating tournament:', error);
    return { error: error.message || 'Failed to create tournament' };
  }
};

export const createTournamentTicketProduct = async (
  tournament: any,
  ticketData: {
    ticket_price: number;
    ticket_quantity?: number;
    max_quantity?: number;
    max_per_user?: number;
    is_active?: boolean;
    image_url?: string | null;
    description?: string | null;
  },
  storeId?: number | string | null,
  userId?: number | string | null
) => {
  try {
    if (!tournament?.id) throw new Error('Torneio inválido');

    let resolvedStoreId = storeId || tournament.store_id;
    let resolvedUserId = userId || tournament.created_by;

    if (!resolvedStoreId || !resolvedUserId) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const profile = await getUserByAuthId(session.user.id);
        if (profile) {
          resolvedUserId = resolvedUserId || profile.id;
          if (!resolvedStoreId) {
            const { data: store } = await supabase
              .from('stores')
              .select('id, store_members!inner(user_id)')
              .eq('store_members.user_id', profile.id)
              .maybeSingle();
            resolvedStoreId = store?.id;
          }
        }
      }
    }

    if (!resolvedStoreId) {
      throw new Error('Loja não encontrada para vincular o ingresso ao estoque.');
    }

    // 0. Call Server-Side API endpoint first (bypasses RLS smoothly using Service Role Key)
    try {
      const response = await fetch('/api/tournaments/ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tournament_id: tournament.id,
          store_id: resolvedStoreId,
          user_id: resolvedUserId,
          ticket_price: ticketData.ticket_price,
          max_quantity: ticketData.max_quantity ?? ticketData.ticket_quantity,
          ticket_quantity: ticketData.max_quantity ?? ticketData.ticket_quantity,
          max_per_user: ticketData.max_per_user,
          is_active: ticketData.is_active,
          image_url: ticketData.image_url,
          description: ticketData.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          return data;
        }
      } else {
        const errJson = await response.json().catch(() => null);
        if (errJson?.error) {
          console.warn('/api/tournaments/ticket returned error:', errJson.error);
        }
      }
    } catch (apiErr) {
      console.warn('API /api/tournaments/ticket unavailable, attempting direct fallback:', apiErr);
    }

    // 1. Resolve Ticket Product Type ID (dynamically or fallback to 52)
    let ticketTypeId = 52;
    try {
      const { data: pt } = await supabase
        .from('product_types')
        .select('id')
        .or('name.ilike.%ticket%,name.ilike.%ingresso%,name.ilike.%inscri%')
        .limit(1)
        .maybeSingle();
      if (pt?.id) ticketTypeId = pt.id;
    } catch (e) {
      console.warn('Fallback product_type_id to 52 for ticket:', e);
    }

    // 2. Check if a ticket product already exists for this tournament in products table
    const productName = `Inscrição: ${tournament.name}`.trim();
    const { data: existingProd } = await supabase
      .from('products')
      .select('*')
      .eq('product_type_id', ticketTypeId)
      .or(`tournament_id.eq.${Number(tournament.id)},and(store_id.eq.${Number(resolvedStoreId)},name.ilike.%${tournament.name}%)`)
      .limit(1)
      .maybeSingle();

    const finalPrice = Math.max(0, Number(ticketData.ticket_price) || 0);
    // A quantidade de ingressos colocada à venda é SEMPRE idêntica ao número de vagas do torneio
    const tournamentCapacity = Math.max(1, Number(tournament.max_players) || 8);
    const finalQuantity = tournamentCapacity;
    const finalMaxPerUser = Math.max(1, Number(ticketData.max_per_user) || 1);
    const finalIsActive = ticketData.is_active !== undefined ? Boolean(ticketData.is_active) : true;

    let product: any = existingProd;

    if (!product) {
      // Generate clean unique slug
      const slugBase = `inscricao-${tournament.name}`
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9 ]/g, '')
        .replace(/\s+/g, '-');
      const slug = `${slugBase}-${Date.now()}`;

      const defaultImg = ticketData.image_url || 
        'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=600';

      const { data: newProd, error: pError } = await supabase
        .from('products')
        .insert({
          name: productName,
          slug,
          description: ticketData.description || `Ingresso e taxa de inscrição para o torneio ${tournament.name}. Garanta sua vaga com antecedência!`,
          image_url: defaultImg,
          game_id: tournament.cardgame_id ? Number(tournament.cardgame_id) : null,
          product_type_id: ticketTypeId,
          msrp: finalPrice,
          store_id: Number(resolvedStoreId),
          tournament_id: Number(tournament.id),
          is_official: false,
          is_available: finalIsActive,
          created_by: resolvedUserId ? Number(resolvedUserId) : null
        })
        .select()
        .maybeSingle();

      if (pError) {
        console.error('Error inserting ticket product:', pError);
        throw new Error(`Falha ao criar o produto do ingresso: ${pError.message}`);
      }
      product = newProd;
    } else {
      // Atualiza preço, associação do torneio e disponibilidade do ingresso existente
      const { data: updatedProd, error: uError } = await supabase
        .from('products')
        .update({
          tournament_id: Number(tournament.id),
          msrp: finalPrice,
          is_available: finalIsActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', product.id)
        .select()
        .maybeSingle();

      if (!uError && updatedProd) {
        product = updatedProd;
      }
    }

    if (!product?.id) {
      throw new Error('Não foi possível obter ou criar o produto do ingresso.');
    }

    // 4. ADD / UPDATE STORE STOCK (store_products)
    const stockRes = await updateStoreStock(
      Number(resolvedStoreId),
      product.id,
      finalQuantity,
      finalPrice,
      false,
      finalMaxPerUser
    );

    if (stockRes && (stockRes as any).error) {
      console.error('Error adding ticket to store stock:', (stockRes as any).error);
      throw new Error(`Ingresso criado, mas houve erro ao adicionar no estoque: ${(stockRes as any).error}`);
    }

    return {
      success: true,
      product,
      quantity: finalQuantity,
      price: finalPrice,
      max_per_user: finalMaxPerUser,
      is_active: finalIsActive
    };
  } catch (error: any) {
    console.error('Error in createTournamentTicketProduct:', error);
    return { error: error.message || 'Erro ao processar ingresso do torneio.' };
  }
};


export const startTournament = async (id: number | string) => {
  try {
    const { data: tourney } = await supabase
      .from('tournaments')
      .select('status')
      .eq('id', Number(id))
      .maybeSingle();

    const cur = (tourney?.status || '').toLowerCase();
    if (cur === 'finished' || cur === 'cancelled' || cur === 'canceled') {
      return { error: 'Não é possível alterar o status de um torneio que já foi finalizado ou cancelado.' };
    }

    // 1. Valida se todos os participantes estão com status 'approved'
    const { data: entries, error: entriesErr } = await supabase
      .from('tournament_entries')
      .select('id, status')
      .eq('tournament_id', id);

    if (entriesErr) throw entriesErr;

    const activeEntries = (entries || []).filter((e: any) => e.status !== 'rejected');
    if (activeEntries.length === 0) {
      return { error: 'O torneio precisa de pelo menos 1 participante aprovado para ser iniciado.' };
    }

    const pending = activeEntries.filter((e: any) => e.status === 'pending');
    if (pending.length > 0) {
      return { error: `Todos os participantes ativos devem estar aprovados para iniciar o torneio (${pending.length} participante(s) pendente(s)).` };
    }

    const { error } = await supabase
      .from('tournaments')
      .update({ status: 'running' })
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error starting tournament:', error);
    return { error: error?.message || 'Falha ao iniciar torneio' };
  }
};


/**
 * Hidrata os participantes do torneio com dados seguros e públicos dos usuários
 * utilizando a função RPC segura `get_safe_users` do Supabase.
 */
export const hydrateEntriesWithSafeUsers = async (entries: any[]) => {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  // Coleta os IDs únicos dos participantes
  const userIds = Array.from(
    new Set(
      entries
        .map((e: any) => e.user_id)
        .filter((uid: any) => uid !== null && uid !== undefined)
    )
  );

  if (userIds.length === 0) {
    return entries;
  }

  const userMap = new Map<string | number, any>();

  try {
    const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any)
      .select('id, username, fullname, avatar')
      .in('id', userIds);

    if (!safeErr && safeUsers && Array.isArray(safeUsers) && safeUsers.length > 0) {
      for (const u of safeUsers) {
        userMap.set(u.id, u);
        userMap.set(String(u.id), u);
      }
    } else if (safeErr) {
      console.warn('Aviso ao buscar dados seguros de usuários com get_safe_users:', safeErr);
    }
  } catch (err) {
    console.warn('Erro ao chamar RPC get_safe_users para participantes:', err);
  }

  // Fallback: caso algum usuário não tenha sido recuperado pelo RPC (ex: cache ou delay), tenta fallback na tabela users
  const missingUserIds = userIds.filter((uid: any) => !userMap.has(uid) && !userMap.has(String(uid)));
  if (missingUserIds.length > 0) {
    try {
      const { data: fallbackUsers } = await supabase
        .from('users')
        .select('id, username, fullname, avatar')
        .in('id', missingUserIds);

      if (fallbackUsers && Array.isArray(fallbackUsers)) {
        for (const fu of fallbackUsers) {
          userMap.set(fu.id, fu);
          userMap.set(String(fu.id), fu);
        }
      }
    } catch (fErr) {
      console.warn('Fallback users query error:', fErr);
    }
  }

  return entries.map((entry: any) => {
    const safeUser = userMap.get(entry.user_id) || userMap.get(String(entry.user_id));
    return {
      ...entry,
      user: safeUser || entry.user || null
    };
  });
};


export const getTournamentEntries = async (id: number | string) => {
  try {
    const { data, error } = await supabase
      .from('tournament_entries')
      .select('*, tournament_deck:tournament_decks(*)')
      .eq('tournament_id', id)
      .order('created_at', { ascending: true });

    let entries = data || [];

    if (error) {
      // Fallback em caso de divergência em relação a tournament_decks
      const { data: fallbackData } = await supabase
        .from('tournament_entries')
        .select('*')
        .eq('tournament_id', id)
        .order('created_at', { ascending: true });
      entries = fallbackData || [];
    }

    // Hidrata todos os inscritos utilizando a função get_safe_users
    return await hydrateEntriesWithSafeUsers(entries);
  } catch (error) {
    console.error('Error fetching entries:', error);
    return [];
  }
};


export const getUserTournamentEntry = async (tournamentId: number | string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return null;

    const { data, error } = await supabase
      .from('tournament_entries')
      .select('*, tournament_deck:tournament_decks(*)')
      .eq('tournament_id', tournamentId)
      .eq('user_id', profile.id)
      .maybeSingle();

    let entry = data;
    if (error) {
      const { data: fallbackData } = await supabase
        .from('tournament_entries')
        .select('*')
        .eq('tournament_id', tournamentId)
        .eq('user_id', profile.id)
        .maybeSingle();
      entry = fallbackData || null;
    }

    if (!entry) return null;

    const hydrated = await hydrateEntriesWithSafeUsers([entry]);
    return hydrated[0] || { ...entry, user: profile };
  } catch (error) {
    console.error('Error getting user tournament entry:', error);
    return null;
  }
};


export const submitOrUpdateTournamentDeck = async (
  tournamentId: number | string,
  userDeckId: string | number
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return { error: 'Você precisa estar logado para enviar sua decklist.' };
    }

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) {
      return { error: 'Perfil de usuário não encontrado.' };
    }

    // 1. Check tournament status safely
    const numericTournamentId = !isNaN(Number(tournamentId)) ? Number(tournamentId) : tournamentId;

    let { data: tournament, error: tErr } = await supabase
      .from('tournaments')
      .select('id, status, starts_at')
      .eq('id', numericTournamentId)
      .maybeSingle();

    if (!tournament && typeof tournamentId === 'string') {
      const { data: stringTourney } = await supabase
        .from('tournaments')
        .select('id, status, starts_at')
        .eq('id', tournamentId)
        .maybeSingle();
      tournament = stringTourney;
    }

    if (tErr) {
      console.error('Error fetching tournament in submitOrUpdateTournamentDeck:', tErr);
    }

    if (!tournament) {
      return { error: 'Torneio não encontrado.' };
    }

    if (tournament.status === 'running' || tournament.status === 'finished') {
      return { error: 'Não é possível enviar ou alterar sua decklist após o início do torneio.' };
    }

    // 2. Fetch source deck from public.user_decks
    const { data: userDeck, error: deckErr } = await supabase
      .from('user_decks')
      .select('id, user_id, name, game_id, data')
      .eq('id', userDeckId)
      .eq('user_id', profile.id)
      .maybeSingle();

    if (deckErr || !userDeck) {
      return { error: 'Decklist selecionada não foi encontrada ou não pertence à sua conta.' };
    }

    // 3. Snapshot insert into public.tournament_decks
    const { data: tournamentDeck, error: insertDeckErr } = await supabase
      .from('tournament_decks')
      .insert({
        tournament_id: tournament.id,
        user_id: profile.id,
        source_deck_id: userDeck.id,
        name: userDeck.name,
        game_id: userDeck.game_id,
        data: userDeck.data
      })
      .select('*')
      .single();

    if (insertDeckErr) {
      console.error('Error inserting tournament_decks snapshot:', insertDeckErr);
      throw insertDeckErr;
    }

    // 4. Link tournament_deck_id in public.tournament_entries
    const { data: updatedEntry, error: updateEntryErr } = await supabase
      .from('tournament_entries')
      .update({
        tournament_deck_id: tournamentDeck.id,
        updated_at: new Date().toISOString()
      })
      .eq('tournament_id', tournament.id)
      .eq('user_id', profile.id)
      .select('*, user:users(*), tournament_deck:tournament_decks(*)')
      .maybeSingle();

    if (updateEntryErr) {
      console.warn('Updated tournament_decks but failed linking tournament_entries:', updateEntryErr);
    }

    return {
      success: true,
      tournamentDeck,
      entry: updatedEntry
    };
  } catch (error: any) {
    console.error('Error submitting tournament deck:', error);
    return { error: error?.message || 'Erro ao enviar/alterar decklist.' };
  }
};


export const getTournamentByTicket = async (ticketCode: string) => {
  try {
    const cleanTicket = ticketCode?.trim().toUpperCase();
    if (!cleanTicket) return null;

    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        games:games!tournaments_cardgame_id_fkey(id, name),
        game_formats:game_formats!tournaments_format_id_fkey(id, name),
        tournament_types:tournament_types!tournaments_tournament_type_id_fkey(id, name),
        tournament_event_categories:tournament_event_categories!tournaments_event_category_id_fkey(id, name),
        game_sets:game_sets!tournaments_tourney_set_id_fkey(id, name, code),
        store:stores(id, name, slug, logo),
        creator:users!tournaments_created_by_fkey(id, username, avatar)
      `)
      .eq('ticket', cleanTicket)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      ...data,
      game_formats: data.game_formats || data.tournament_formats,
      tournament_formats: data.game_formats || data.tournament_formats,
      start_date: data.starts_at || data.start_date
    };
  } catch (error) {
    console.error('Error fetching tournament by ticket:', error);
    return null;
  }
};


export const joinTournamentWithTicket = async (
  ticketCode: string, 
  options?: { tournamentId?: number | string; deckId?: string | null }
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return { error: 'Você precisa estar conectado à sua conta para ingressar no torneio.' };
    }

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) {
      return { error: 'Perfil de usuário não encontrado.' };
    }

    const cleanTicket = ticketCode?.trim().toUpperCase();
    if (!cleanTicket) {
      return { error: 'Por favor, informe o código do ticket.' };
    }

    // Find the tournament
    let query = supabase.from('tournaments').select('*');
    if (options?.tournamentId) {
      query = query.eq('id', options.tournamentId);
    } else {
      query = query.eq('ticket', cleanTicket);
    }

    const { data: tournament, error: tErr } = await query.maybeSingle();
    if (tErr) throw tErr;

    if (!tournament) {
      return { error: 'Código de ticket incorreto ou nenhum torneio encontrado.' };
    }

    // Verify ticket code if querying by ID
    if (options?.tournamentId && tournament.ticket && tournament.ticket.trim().toUpperCase() !== cleanTicket) {
      return { error: 'Código do ticket incorreto para este torneio. Verifique com o organizador.' };
    }

    if (tournament.status === 'finished') {
      return { error: 'Este torneio já foi finalizado e não aceita novos participantes.' };
    }

    // Check if user is already enrolled
    const { data: existingEntry, error: eErr } = await supabase
      .from('tournament_entries')
      .select('*')
      .eq('tournament_id', tournament.id)
      .eq('user_id', profile.id)
      .maybeSingle();

    if (existingEntry) {
      return { 
        error: 'Você já está inscrito neste torneio!', 
        alreadyEnrolled: true,
        entry: existingEntry,
        tournament 
      };
    }

    // Check max_players capacity
    const { count, error: countErr } = await supabase
      .from('tournament_entries')
      .select('*', { count: 'exact', head: true })
      .eq('tournament_id', tournament.id);

    if (tournament.max_players && count !== null && count >= tournament.max_players) {
      return { error: `Este torneio já atingiu a capacidade máxima de ${tournament.max_players} participantes.` };
    }

    // If user provided a deckId, snapshot it to tournament_decks
    let linkedTournamentDeckId: string | null = null;
    if (options?.deckId) {
      const { data: userDeck } = await supabase
        .from('user_decks')
        .select('id, user_id, name, game_id, data')
        .eq('id', options.deckId)
        .eq('user_id', profile.id)
        .maybeSingle();

      if (userDeck) {
        const { data: createdDeck, error: cDeckErr } = await supabase
          .from('tournament_decks')
          .insert({
            tournament_id: tournament.id,
            user_id: profile.id,
            source_deck_id: userDeck.id,
            name: userDeck.name,
            game_id: userDeck.game_id,
            data: userDeck.data
          })
          .select('id')
          .maybeSingle();

        if (!cDeckErr && createdDeck) {
          linkedTournamentDeckId = createdDeck.id;
        }
      }
    }

    // Insert into tournament_entries
    const { data: newEntry, error: insertErr } = await supabase
      .from('tournament_entries')
      .insert({
        tournament_id: tournament.id,
        user_id: profile.id,
        status: 'approved',
        points: 0,
        tournament_deck_id: linkedTournamentDeckId,
        updated_at: new Date().toISOString()
      })
      .select('*, tournament_deck:tournament_decks(*)')
      .maybeSingle();

    if (insertErr) {
      if (insertErr.code === '23505') {
        return { error: 'Você já está inscrito neste torneio!', alreadyEnrolled: true, tournament };
      }
      throw insertErr;
    }

    const hydrated = await hydrateEntriesWithSafeUsers([newEntry || {}]);

    return { 
      success: true, 
      tournament, 
      entry: hydrated[0] || { ...newEntry, user: profile } 
    };
  } catch (error: any) {
    console.error('Error joining tournament with ticket:', error);
    return { error: error?.message || 'Erro ao ingressar no torneio.' };
  }
};


export const cancelTournamentEntry = async (tournamentId: number | string, targetUserId?: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return { error: 'Você precisa estar logado.' };
    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return { error: 'Perfil não encontrado.' };

    // Validar se o usuário é organizador do torneio ou administrador
    const { data: tourney } = await supabase
      .from('tournaments')
      .select('id, created_by, store_id')
      .eq('id', tournamentId)
      .single();

    const isOrganizer = tourney && (
      tourney.created_by === profile.id ||
      profile.role_id === 1 ||
      (profile.store_id && tourney.store_id === profile.store_id)
    );

    if (!isOrganizer) {
      return { error: 'Apenas o organizador do torneio pode alterar ou cancelar inscrições.' };
    }

    const userIdToDelete = targetUserId || profile.id;
    const { error } = await supabase
      .from('tournament_entries')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('user_id', userIdToDelete);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling tournament entry:', error);
    return { error: error?.message || 'Erro ao cancelar inscrição.' };
  }
};

export const removeTournamentEntry = async (entryId: number | string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return { error: 'Você precisa estar logado.' };
    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return { error: 'Perfil não encontrado.' };

    // Buscar a inscrição e dados do torneio para verificar se o usuário é organizador
    const { data: entry } = await supabase
      .from('tournament_entries')
      .select('id, tournament_id, user_id, tournaments(id, created_by, store_id, status)')
      .eq('id', entryId)
      .maybeSingle();

    if (!entry) {
      return { error: 'Inscrição não encontrada.' };
    }

    const tourney = (entry as any).tournaments;
    if (tourney && tourney.status && tourney.status !== 'open' && tourney.status !== 'scheduled') {
      return { error: 'Não é possível remover participantes após o início do torneio.' };
    }

    const isOrganizer = tourney && (
      tourney.created_by === profile.id ||
      profile.role_id === 1 ||
      (profile.store_id && tourney.store_id === profile.store_id)
    );

    if (!isOrganizer) {
      return { error: 'Um participante não pode cancelar sua própria inscrição. Apenas o organizador do torneio pode alterar ou cancelar inscrições.' };
    }

    const { error } = await supabase
      .from('tournament_entries')
      .delete()
      .eq('id', entryId);

    if (error) {
      // Fallback via server API
      const apiRes = await fetch('/api/tournaments/remove-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entryId })
      });
      if (apiRes.ok) return { success: true };
      throw error;
    }
    return { success: true };
  } catch (error: any) {
    try {
      const apiRes = await fetch('/api/tournaments/remove-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entryId })
      });
      if (apiRes.ok) return { success: true };
    } catch (_) {}
    console.error('Error removing tournament entry:', error);
    return { error: error?.message || 'Falha ao cancelar ou remover inscrição do participante.' };
  }
};


export const updateEntryStatus = async (entryId: number | string, status: string) => {
  try {
    const { data: entry } = await supabase
      .from('tournament_entries')
      .select('id, tournament_id, tournaments(status)')
      .eq('id', entryId)
      .maybeSingle();

    const tourneyStatus = (entry as any)?.tournaments?.status;
    if (tourneyStatus && tourneyStatus !== 'open' && tourneyStatus !== 'scheduled') {
      return { error: 'Não é possível alterar o status dos participantes após o início do torneio.' };
    }

    const { data: updatedRows, error } = await supabase
      .from('tournament_entries')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .select();

    if (error || !updatedRows || updatedRows.length === 0) {
      // Fallback via endpoint do servidor (Service Role) para garantir persistência 100% no banco
      const apiRes = await fetch('/api/tournaments/entry-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entryId, status })
      });
      if (apiRes.ok) {
        return { success: true };
      }
      if (error) throw error;
    }

    return { success: true };
  } catch (error: any) {
    try {
      const apiRes = await fetch('/api/tournaments/entry-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entryId, status })
      });
      if (apiRes.ok) {
        return { success: true };
      }
    } catch (_) {}
    console.error('Error updating entry status:', error);
    return { error: error?.message || 'Falha ao atualizar status do participante.' };
  }
};


export const finalizeTournament = async (id: number | string, tops?: { top1?: string; top2?: string; top3?: string }) => {
  try {
    const { data: tourney } = await supabase
      .from('tournaments')
      .select('status')
      .eq('id', Number(id))
      .maybeSingle();

    const cur = (tourney?.status || '').toLowerCase();
    if (cur === 'finished' || cur === 'cancelled' || cur === 'canceled') {
      return { error: 'Este torneio já foi finalizado ou cancelado e seu status não pode ser alterado.' };
    }

    const { error } = await supabase
      .from('tournaments')
      .update({ 
        status: 'finished'
      })
      .eq('id', id);
    
    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error finalizing tournament:', error);
    return { error: error?.message || 'Falha ao finalizar torneio' };
  }
};


export const cancelTournament = async (id: number | string, reason?: string) => {
  try {
    const { data: tourney } = await supabase
      .from('tournaments')
      .select('status')
      .eq('id', Number(id))
      .maybeSingle();

    const cur = (tourney?.status || '').toLowerCase();
    if (cur === 'finished' || cur === 'cancelled' || cur === 'canceled') {
      return { error: 'Este torneio já foi finalizado ou cancelado e seu status não pode ser alterado.' };
    }

    // Atualiza o status do torneio para 'cancelled' (ou 'canceled' como fallback)
    const { error } = await supabase
      .from('tournaments')
      .update({ 
        status: 'cancelled'
      })
      .eq('id', id);
    
    if (error) {
      console.warn('Tentando fallback com status canceled:', error);
      const { error: fallbackError } = await supabase
        .from('tournaments')
        .update({ 
          status: 'canceled'
        })
        .eq('id', id);
      if (fallbackError) throw fallbackError;
    }
    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling tournament:', error);
    return { error: error?.message || 'Falha ao cancelar o torneio' };
  }
};


/**
 * Permite ao lojista alterar o status de qualquer torneio que não seja finished ou cancelled.
 * Status suportados: 'scheduled' | 'open' | 'in_progress' | 'finished' | 'cancelled'
 */
export const updateTournamentStatus = async (
  id: number | string,
  newStatus: string
) => {
  try {
    const { data: tourney, error: fetchErr } = await supabase
      .from('tournaments')
      .select('id, status')
      .eq('id', Number(id))
      .single();

    if (fetchErr || !tourney) {
      throw new Error('Torneio não encontrado.');
    }

    const currentStatus = (tourney.status || '').toLowerCase();
    if (currentStatus === 'finished' || currentStatus === 'cancelled' || currentStatus === 'canceled') {
      return { error: 'Não é possível alterar o status de um torneio que já foi finalizado ou cancelado.' };
    }

    let statusToSet = newStatus.toLowerCase();
    let { error } = await supabase
      .from('tournaments')
      .update({ status: statusToSet })
      .eq('id', Number(id));

    // Fallbacks para variações de valores no banco (ex: running vs in_progress)
    if (error && (statusToSet === 'in_progress' || statusToSet === 'running')) {
      const alt = statusToSet === 'in_progress' ? 'running' : 'in_progress';
      const altRes = await supabase
        .from('tournaments')
        .update({ status: alt })
        .eq('id', Number(id));
      if (!altRes.error) {
        error = null;
        statusToSet = alt;
      }
    }

    if (error && (statusToSet === 'cancelled' || statusToSet === 'canceled')) {
      const alt = statusToSet === 'cancelled' ? 'canceled' : 'cancelled';
      const altRes = await supabase
        .from('tournaments')
        .update({ status: alt })
        .eq('id', Number(id));
      if (!altRes.error) {
        error = null;
        statusToSet = alt;
      }
    }

    if (error) throw error;
    return { success: true, status: statusToSet };
  } catch (error: any) {
    console.error('Error updating tournament status:', error);
    return { error: error?.message || 'Falha ao alterar status do torneio.' };
  }
};


export const getTournamentDetails = async (id: number | string) => {
  try {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *, 
        games:games!tournaments_cardgame_id_fkey(id, name), 
        game_formats:game_formats!tournaments_format_id_fkey(id, name),
        tournament_types:tournament_types!tournaments_tournament_type_id_fkey(id, name),
        tournament_event_categories:tournament_event_categories!tournaments_event_category_id_fkey(id, name),
        game_sets:game_sets!tournaments_tourney_set_id_fkey(id, name, code),
        store:stores(
          id,
          name,
          slug,
          logo
        ),
        creator:users!tournaments_created_by_fkey(id, username, avatar)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    // Hidrata criador com get_safe_users se não retornado devido a RLS
    if (data.created_by && !data.creator) {
      try {
        const { data: safeCreators } = await (supabase.rpc('get_safe_users') as any)
          .select('id, username, fullname, avatar')
          .eq('id', data.created_by)
          .limit(1);
        if (safeCreators && safeCreators.length > 0) {
          data.creator = safeCreators[0];
        }
      } catch (cErr) {
        console.warn('Could not fetch tournament creator via get_safe_users:', cErr);
      }
    }

    let mappedTickets: any[] = [];
    try {
      const { data: ticketProds } = await supabase
        .from('products')
        .select('*, store_products(*)')
        .eq('product_type_id', 52)
        .eq('is_available', true)
        .ilike('name', `%${data.name}%`);

      if (ticketProds && ticketProds.length > 0) {
        // Obter contagem real de inscritos na tabela tournament_entries
        const { data: entriesForTourney } = await supabase
          .from('tournament_entries')
          .select('id, status')
          .eq('tournament_id', data.id);

        const filledSpots = (entriesForTourney || []).filter((e: any) => e.status !== 'rejected').length;

        mappedTickets = ticketProds
          .filter((tp: any) => tp.is_available !== false)
          .map((tp: any) => {
            const sp = Array.isArray(tp.store_products) && tp.store_products.length > 0 ? tp.store_products[0] : null;
            const price = sp?.store_price ?? tp.msrp ?? 0;
            const maxQ = Number(data.max_players) || Number(sp?.quantity) || 8;
            return {
              id: tp.id,
              product_id: tp.id,
              tournament_id: data.id,
              max_quantity: maxQ,
              sold_quantity: filledSpots,
              is_active: tp.is_available !== false,
              is_available: tp.is_available !== false,
              max_per_user: sp?.max_per_user ?? 1,
              product: {
                ...tp,
                msrp: price,
                mspr: price
              }
            };
          });
      }
    } catch (tErr) {
      console.warn('Could not load ticket products for tournament details:', tErr);
    }

    return {
      ...data,
      game_formats: data.game_formats || data.tournament_formats,
      tournament_formats: data.game_formats || data.tournament_formats,
      tickets: mappedTickets,
      start_date: data.starts_at || data.start_date
    };
  } catch (error) {
    console.error('Error fetching tournament details with joins, trying fallback:', error);
    try {
      const { data: fbData, error: fbError } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (fbError) throw fbError;
      if (!fbData) return null;
      return {
        ...fbData,
        start_date: fbData.starts_at || fbData.start_date
      };
    } catch (finalErr) {
      console.error('Error in getTournamentDetails final fallback:', finalErr);
      return null;
    }
  }
};

// Fetches available tournament ticket products directly from products and matches with tournaments and tournament_entries
export const getTournamentTickets = async () => {
  try {
    const [
      { data: ticketProducts, error: pError },
      { data: tourneys, error: tError },
      { data: entriesData, error: eError }
    ] = await Promise.all([
      supabase
        .from('products')
        .select(`
          id,
          name,
          beauty_name,
          description,
          image_url,
          msrp,
          store_id,
          game_id,
          product_type_id,
          is_available,
          tournament_id,
          games:games(id, name),
          store:stores(id, name, logo, slug, is_partner, sales_enabled),
          store_products(id, quantity, reserved_quantity, store_price, pre_sale, max_per_user)
        `)
        .eq('product_type_id', 52)
        .eq('is_available', true)
        .order('id', { ascending: false }),
      supabase
        .from('tournaments')
        .select(`
          id,
          name,
          ticket,
          starts_at,
          ends_at,
          status,
          max_players,
          current_round,
          format_id,
          cardgame_id,
          store_id,
          games:games!tournaments_cardgame_id_fkey(id, name),
          game_formats:game_formats!tournaments_format_id_fkey(id, name),
          store:stores(id, name, logo, slug, is_partner, sales_enabled)
        `),
      supabase
        .from('tournament_entries')
        .select('id, tournament_id, status')
    ]);

    if (pError) {
      console.error('Error fetching ticket products:', pError);
      return [];
    }

    if (eError) {
      console.warn('Warning fetching tournament_entries in getTournamentTickets:', eError);
    }

    const tournamentsList = tourneys || [];

    // Mapeia vagas preenchidas a partir da tabela tournament_entries
    const entriesCountMap = new Map<number, number>();
    (entriesData || []).forEach((entry: any) => {
      if (entry.status !== 'rejected') {
        const tid = Number(entry.tournament_id);
        if (tid) {
          entriesCountMap.set(tid, (entriesCountMap.get(tid) || 0) + 1);
        }
      }
    });

    const result = (ticketProducts || [])
      .filter((prod: any) => prod.is_available !== false)
      .map((prod: any) => {
        const cleanProdName = (prod.name || '').toLowerCase().replace(/^(inscrição:|ingresso:|ticket:)\s*/i, '').trim();
        const matchedTourney = prod.tournament_id
          ? tournamentsList.find((t: any) => Number(t.id) === Number(prod.tournament_id))
          : (tournamentsList.find((t: any) => {
              const cleanTName = (t.name || '').toLowerCase().trim();
              return cleanTName === cleanProdName || cleanProdName.includes(cleanTName) || cleanTName.includes(cleanProdName);
            }) || (prod.store_id ? tournamentsList.find((t: any) => t.store_id === prod.store_id) : null));

        const sp = Array.isArray(prod.store_products) && prod.store_products.length > 0 ? prod.store_products[0] : null;

        // Vagas totais do torneio: capacidade máxima definida no evento (max_players)
        const maxQuantity = Number(matchedTourney?.max_players) || Number(sp?.quantity) || 8;

        // Vagas preenchidas extraídas diretamente da tabela tournament_entries
        const tourneyId = matchedTourney?.id ? Number(matchedTourney.id) : (prod.tournament_id ? Number(prod.tournament_id) : null);
        const soldQuantity = tourneyId ? (entriesCountMap.get(tourneyId) || 0) : 0;
        const availableQuantity = maxQuantity > 0 ? Math.max(0, maxQuantity - soldQuantity) : 999;
        const storePrice = sp?.store_price ?? prod.msrp ?? 0;

        const productWithPrice = {
          ...prod,
          msrp: storePrice,
          price: storePrice
        };

        return {
          id: prod.id,
          tournament_id: tourneyId,
          product_id: prod.id,
          max_quantity: maxQuantity,
          sold_quantity: soldQuantity,
          available_quantity: availableQuantity,
          is_active: prod.is_available !== false,
          is_available: prod.is_available !== false,
          max_per_user: sp?.max_per_user ?? 1,
          product: productWithPrice,
          tournament: matchedTourney || null
        };
      });

    return result;
  } catch (err) {
    console.error('Error fetching tournament tickets:', err);
    return [];
  }
};

export const getTicketProducts = async () => {
  try {
    const validTickets = await getTournamentTickets();
    return validTickets.map((t: any) => ({
      ...t.product,
      tournament: t.tournament,
      games: t.product?.games || t.tournament?.games,
      store: t.product?.store || t.tournament?.store
    }));
  } catch (err) {
    console.error('Error fetching ticket products:', err);
    return [];
  }
};

export interface UserPurchasedTicket {
  id: string;
  orderId?: number | string;
  orderStatus?: string;
  orderCreatedAt?: string;
  ticketPrice: number;
  quantity: number;
  productId?: number;
  productName: string;
  productImage?: string;
  tournamentId: number;
  tournamentName: string;
  tournamentCode: string;
  tournamentStatus: string;
  tournamentStartsAt?: string;
  tournamentEndsAt?: string;
  storeName?: string;
  storeLogo?: string;
  storeSlug?: string;
  gameName?: string;
  formatName?: string;
  isEnrolled: boolean;
  entryDate?: string;
}

export const getUserPurchasedTickets = async (): Promise<UserPurchasedTicket[]> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return [];

    // 1. Fetch user orders
    const { data: orders, error: ordersErr } = await supabase
      .from('orders')
      .select(`
        id,
        status,
        created_at,
        amount,
        products_total,
        order_items(
          id,
          product_id,
          quantity,
          unit_price,
          product:products(
            id,
            name,
            image_url,
            msrp,
            games:games(id, name)
          )
        ),
        store:stores!fk_orders_store(id, name, logo, slug)
      `)
      .eq('buyer_id', profile.id)
      .order('created_at', { ascending: false });

    if (ordersErr) {
      console.error('Error fetching orders in getUserPurchasedTickets:', ordersErr);
    }

    const activeOrders = (orders as any[] || []).filter(o => 
      !['cancelled', 'canceled', 'expired', 'refunded'].includes(o.status)
    );

    const productIds = activeOrders.flatMap(o => 
      (o.order_items || []).map((oi: any) => Number(oi.product_id))
    ).filter(Boolean);

    // 2. Fetch ticket products matching these products
    const ttMap = new Map<number, any>();
    if (productIds.length > 0) {
      try {
        const [{ data: ticketProds, error: pErr }, { data: allTourneys, error: tErr }] = await Promise.all([
          supabase
            .from('products')
            .select(`
              id,
              name,
              beauty_name,
              image_url,
              msrp,
              store_id,
              game_id,
              games:games(id, name),
              store:stores(id, name, logo, slug)
            `)
            .in('id', productIds)
            .eq('product_type_id', 52),
          supabase
            .from('tournaments')
            .select(`
              id,
              name,
              ticket,
              starts_at,
              ends_at,
              status,
              max_players,
              current_round,
              cardgame_id,
              format_id,
              games:games!tournaments_cardgame_id_fkey(id, name),
              game_formats:game_formats!tournaments_format_id_fkey(id, name),
              store:stores(id, name, logo, slug)
            `)
        ]);

        if (!pErr && ticketProds) {
          const tournamentsList = allTourneys || [];
          (ticketProds as any[]).forEach((tp: any) => {
            const cleanProdName = (tp.name || '').toLowerCase().replace(/^(inscrição:|ingresso:|ticket:)\s*/i, '').trim();
            const matchedTourney = tournamentsList.find((t: any) => {
              const cleanTName = (t.name || '').toLowerCase().trim();
              return cleanTName === cleanProdName || cleanProdName.includes(cleanTName) || cleanTName.includes(cleanProdName);
            }) || (tp.store_id ? tournamentsList.find((t: any) => t.store_id === tp.store_id) : null);

            ttMap.set(Number(tp.id), {
              id: tp.id,
              product_id: tp.id,
              tournament_id: matchedTourney?.id ?? null,
              tournament: matchedTourney || null,
              product: tp
            });
          });
        }
      } catch (ttErr) {
        console.error('Error fetching ticket products for user orders:', ttErr);
      }
    }

    // 3. Fetch tournament_entries for this user
    const { data: entries, error: entriesErr } = await supabase
      .from('tournament_entries')
      .select(`
        id,
        tournament_id,
        created_at,
        tournament:tournaments(
          id,
          name,
          ticket,
          starts_at,
          ends_at,
          status,
          max_players,
          current_round,
          games:games!tournaments_cardgame_id_fkey(id, name),
          game_formats:game_formats!tournaments_format_id_fkey(id, name),
          store:stores(id, name, logo, slug)
        )
      `)
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });

    if (entriesErr) {
      console.error('Error fetching tournament entries in getUserPurchasedTickets:', entriesErr);
    }

    const entriesMap = new Map<number, any>(
      ((entries as any[]) || []).map((e: any) => [Number(e.tournament_id), e])
    );

    const result: UserPurchasedTicket[] = [];
    const seenTournamentIds = new Set<number>();

    // Add tickets from orders
    for (const order of activeOrders) {
      const orderStore: any = Array.isArray(order.store) ? order.store[0] : order.store;

      for (const item of ((order.order_items as any[]) || [])) {
        const prodId = Number(item.product_id);
        if (ttMap.has(prodId)) {
          const tt = ttMap.get(prodId);
          const tourney: any = Array.isArray(tt.tournament) ? tt.tournament[0] : tt.tournament;
          if (!tourney) continue;

          const prod: any = Array.isArray(item.product) ? item.product[0] : item.product;
          const tourneyStore: any = Array.isArray(tourney.store) ? tourney.store[0] : tourney.store;
          const tourneyGame: any = Array.isArray(tourney.games) ? tourney.games[0] : tourney.games;
          const prodGame: any = prod && Array.isArray(prod.games) ? prod.games[0] : prod?.games;
          const tourneyFormat: any = Array.isArray(tourney.game_formats) ? tourney.game_formats[0] : tourney.game_formats;

          const entry = entriesMap.get(Number(tourney.id));
          const tCode = tourney.ticket?.trim() || `TRN-${tourney.id}`;

          seenTournamentIds.add(Number(tourney.id));

          result.push({
            id: `order-${order.id}-${item.id}`,
            orderId: order.id,
            orderStatus: order.status,
            orderCreatedAt: order.created_at,
            ticketPrice: Number(item.unit_price || item.price || prod?.msrp || 0),
            quantity: Number(item.quantity || 1),
            productId: prodId,
            productName: prod?.name || 'Ingresso de Torneio',
            productImage: prod?.image_url,
            tournamentId: Number(tourney.id),
            tournamentName: tourney.name || 'Torneio Cardumy',
            tournamentCode: tCode,
            tournamentStatus: tourney.status || 'open',
            tournamentStartsAt: tourney.starts_at,
            tournamentEndsAt: tourney.ends_at,
            storeName: tourneyStore?.name || orderStore?.name || 'Loja Parceira',
            storeLogo: tourneyStore?.logo || orderStore?.logo,
            storeSlug: tourneyStore?.slug || orderStore?.slug,
            gameName: tourneyGame?.name || prodGame?.name || 'TCG',
            formatName: tourneyFormat?.name,
            isEnrolled: !!entry,
            entryDate: entry?.created_at
          });
        }
      }
    }

    // Add any remaining tournaments where user has an entry
    for (const entry of ((entries as any[]) || [])) {
      const tourney: any = Array.isArray(entry.tournament) ? entry.tournament[0] : entry.tournament;
      if (tourney && !seenTournamentIds.has(Number(tourney.id))) {
        seenTournamentIds.add(Number(tourney.id));
        const tCode = tourney.ticket?.trim() || `TRN-${tourney.id}`;
        const tourneyStore: any = Array.isArray(tourney.store) ? tourney.store[0] : tourney.store;
        const tourneyGame: any = Array.isArray(tourney.games) ? tourney.games[0] : tourney.games;
        const tourneyFormat: any = Array.isArray(tourney.game_formats) ? tourney.game_formats[0] : tourney.game_formats;

        result.push({
          id: `entry-${entry.id}`,
          orderStatus: 'confirmed',
          orderCreatedAt: entry.created_at,
          ticketPrice: 0,
          quantity: 1,
          productName: `Inscrição - ${tourney.name}`,
          tournamentId: Number(tourney.id),
          tournamentName: tourney.name || 'Torneio Cardumy',
          tournamentCode: tCode,
          tournamentStatus: tourney.status || 'open',
          tournamentStartsAt: tourney.starts_at,
          tournamentEndsAt: tourney.ends_at,
          storeName: tourneyStore?.name || 'Loja Parceira',
          storeLogo: tourneyStore?.logo,
          storeSlug: tourneyStore?.slug,
          gameName: tourneyGame?.name || 'TCG',
          formatName: tourneyFormat?.name,
          isEnrolled: true,
          entryDate: entry.created_at
        });
      }
    }

    return result;
  } catch (err) {
    console.error('Error in getUserPurchasedTickets:', err);
    return [];
  }
};

export interface SaveTournamentImportParams {
  tournamentId?: number | string | null;
  gameId?: number | string | null;
  source?: string;
  filename: string;
  fileText: string;
  rows: Array<{
    ranking: number | string;
    membershipNumber: string;
    userName: string;
    winPoints: number | string;
    omw: string;
    oomw: string;
    memo?: string;
    deckUrls?: string;
  }>;
}

export const saveTournamentImport = async ({
  tournamentId,
  gameId,
  source,
  filename,
  fileText,
  rows
}: SaveTournamentImportParams) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Usuário não autenticado.');
    }

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) {
      throw new Error('Perfil do usuário não encontrado.');
    }

    let fileHash = 'hash_' + Date.now();
    try {
      if (typeof crypto !== 'undefined' && crypto.subtle) {
        const msgBuffer = new TextEncoder().encode(fileText);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        fileHash = Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
      }
    } catch (e) {
      console.warn('Erro ao calcular hash SHA-256, usando fallback', e);
    }

    // Verificar se este arquivo exato (pelo hash do conteúdo) já foi importado antes
    try {
      const { data: existingImport } = await supabase
        .from('tournament_imports')
        .select('id, filename, created_at, tournament:tournaments(name)')
        .eq('file_hash', fileHash)
        .limit(1)
        .maybeSingle();

      if (existingImport) {
        const formattedDate = new Date(existingImport.created_at).toLocaleDateString('pt-BR');
        const tourneyInfo = (existingImport as any).tournament?.name 
          ? ` (no torneio "${(existingImport as any).tournament.name}")` 
          : '';
        return { 
          error: `Este arquivo já foi importado anteriormente em ${formattedDate} com o nome "${existingImport.filename}"${tourneyInfo}. Não é permitido enviar o mesmo arquivo duas vezes.` 
        };
      }
    } catch (checkErr) {
      // Tentar na tabela fallback se a anterior falhar
      try {
        const { data: fbExisting } = await supabase
          .from('tournament_import')
          .select('id, filename, created_at')
          .eq('file_hash', fileHash)
          .limit(1)
          .maybeSingle();

        if (fbExisting) {
          const formattedDate = new Date(fbExisting.created_at).toLocaleDateString('pt-BR');
          return { 
            error: `Este arquivo já foi importado anteriormente em ${formattedDate} com o nome "${fbExisting.filename}". Não é permitido enviar o mesmo arquivo duas vezes.` 
          };
        }
      } catch (fbErr) {
        console.warn('Erro ao checar duplicidade de arquivo:', fbErr);
      }
    }

    const memberNumbers = rows.map((r) => String(r.membershipNumber || '').trim()).filter(Boolean);
    const matchedUsersMap = new Map<string, any>();
    if (memberNumbers.length > 0) {
      const { data: matchedUsers } = await supabase
        .from('users')
        .select('id, username, bandai_member')
        .in('bandai_member', memberNumbers);

      if (matchedUsers) {
        matchedUsers.forEach((u) => {
          if (u.bandai_member) {
            matchedUsersMap.set(String(u.bandai_member).trim(), u);
          }
        });
      }
    }

    const matchedCount = rows.filter((r) => matchedUsersMap.has(String(r.membershipNumber || '').trim())).length;

    const importPayload: any = {
      imported_by: profile.id,
      source: source || 'bandai_tcg_plus',
      filename: filename || 'classificacao.csv',
      file_hash: fileHash,
      status: 'completed',
      is_active: !!tournamentId,
      total_rows: rows.length,
      matched_rows: matchedCount,
      completed_at: new Date().toISOString()
    };

    if (gameId && Number(gameId) > 0) {
      importPayload.game_id = Number(gameId);
    }

    if (tournamentId && Number(tournamentId) > 0) {
      importPayload.tournament_id = Number(tournamentId);
    }

    let importRecord: any = null;

    const { data: insertedImport, error: insertImportError } = await supabase
      .from('tournament_imports')
      .insert(importPayload)
      .select()
      .single();

    if (insertImportError) {
      const { data: fallbackImport, error: fallbackError } = await supabase
        .from('tournament_import')
        .insert(importPayload)
        .select()
        .single();

      if (fallbackError) {
        console.error('Error inserting into tournament_imports:', insertImportError, fallbackError);
        throw new Error(insertImportError.message || fallbackError.message);
      }
      importRecord = fallbackImport;
    } else {
      importRecord = insertedImport;
    }

    if (!importRecord?.id) {
      throw new Error('Falha ao obter ID da importação criada.');
    }

    const resultsPayload = rows.map((r, idx) => {
      const cleanMember = String(r.membershipNumber || '').trim();
      const matched = matchedUsersMap.get(cleanMember);
      const omwVal = parseFloat(String(r.omw || '').replace('%', ''));
      const oomwVal = parseFloat(String(r.oomw || '').replace('%', ''));

      return {
        import_id: importRecord.id,
        ranking: Number(r.ranking) || idx + 1,
        membership_number: cleanMember,
        player_name: String(r.userName || '').trim() || 'Jogador',
        win_points: Math.max(0, Number(r.winPoints) || 0),
        omw_pct: !isNaN(omwVal) ? Math.min(100, Math.max(0, omwVal)) : null,
        oomw_pct: !isNaN(oomwVal) ? Math.min(100, Math.max(0, oomwVal)) : null,
        memo: r.memo && r.memo !== 'undefined' ? r.memo : null,
        deck_urls: r.deckUrls && r.deckUrls !== 'undefined' ? r.deckUrls : null,
        match_status: matched ? 'matched' : 'unmatched',
        user_id: matched ? matched.id : null,
        raw_data: {
          ranking: r.ranking,
          membershipNumber: r.membershipNumber,
          userName: r.userName,
          winPoints: r.winPoints,
          omw: r.omw,
          oomw: r.oomw
        }
      };
    });

    const { error: resultsError } = await supabase
      .from('tournament_import_results')
      .insert(resultsPayload);

    if (resultsError) {
      console.error('Error inserting into tournament_import_results:', resultsError);
      throw new Error(`Importação criada, mas ocorreu erro ao salvar as linhas: ${resultsError.message}`);
    }

    return {
      success: true,
      importId: importRecord.id,
      totalRows: rows.length,
      matchedRows: matchedCount
    };
  } catch (error: any) {
    console.error('Error in saveTournamentImport:', error);
    return { error: error.message || 'Erro ao salvar importação de torneio.' };
  }
};

export const getTournamentImports = async (tournamentId?: number | string | null) => {
  try {
    let query = supabase
      .from('tournament_imports')
      .select('*, tournament:tournaments(id, name, starts_at), game:games(id, name, slug)')
      .order('created_at', { ascending: false });

    if (tournamentId) {
      query = query.eq('tournament_id', Number(tournamentId));
    }

    const { data, error } = await query;
    if (error) {
      const { data: fbData, error: fbError } = await supabase
        .from('tournament_import')
        .select('*, tournament:tournaments(id, name, starts_at), game:games(id, name, slug)')
        .order('created_at', { ascending: false });
      if (fbError) return [];
      return fbData || [];
    }
    return data || [];
  } catch (err) {
    console.error('Error in getTournamentImports:', err);
    return [];
  }
};

export const getTournamentImportResults = async (importId: number | string) => {
  try {
    const { data, error } = await supabase
      .from('tournament_import_results')
      .select('*, user:users(id, username, avatar)')
      .eq('import_id', Number(importId))
      .order('ranking', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error in getTournamentImportResults:', err);
    return [];
  }
};

/**
 * Adiciona 1 vaga ao torneio, sincronizando obrigatoriamente
 * tournaments.max_players e a quantity do produto associado em store_products.
 * Ambos os valores nunca devem divergir.
 */
export const addTournamentSlot = async (tournamentId: number | string, storeId?: number | string) => {
  try {
    // 1. Tenta rota backend com privilégios de serviço
    try {
      const response = await fetch('/api/tournaments/add-slot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: Number(tournamentId),
          store_id: storeId ? Number(storeId) : undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          return data;
        }
      }
    } catch (apiErr) {
      console.warn('API /api/tournaments/add-slot indisponível, usando fallback direto Supabase:', apiErr);
    }

    // 2. Fallback direto Supabase
    const { data: tourney, error: tErr } = await supabase
      .from('tournaments')
      .select('*')
      .eq('id', tournamentId)
      .single();

    if (tErr || !tourney) {
      throw new Error(tErr?.message || 'Torneio não encontrado.');
    }

    if (tourney.status !== 'open' && tourney.status !== 'scheduled') {
      throw new Error('Não é possível adicionar vagas após o início do torneio.');
    }

    const newSlots = (Number(tourney.max_players) || 0) + 1;

    // Atualiza tournaments.max_players
    const { data: updatedTourney, error: uErr } = await supabase
      .from('tournaments')
      .update({
        max_players: newSlots
      })
      .eq('id', tournamentId)
      .select()
      .single();

    if (uErr) {
      throw new Error('Erro ao atualizar vagas do torneio: ' + uErr.message);
    }

    const resolvedStoreId = Number(storeId || tourney.store_id || 1);

    // Localiza produto de ingresso vinculado
    let ticketProduct: any = null;
    const { data: byTournamentId } = await supabase
      .from('products')
      .select('*')
      .eq('product_type_id', 52)
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    if (byTournamentId) {
      ticketProduct = byTournamentId;
    } else {
      const { data: byName } = await supabase
        .from('products')
        .select('*')
        .eq('product_type_id', 52)
        .ilike('name', `%${tourney.name}%`)
        .limit(1)
        .maybeSingle();

      if (byName) {
        ticketProduct = byName;
        await supabase
          .from('products')
          .update({ tournament_id: tourney.id })
          .eq('id', byName.id);
      }
    }

    let stockData = null;
    if (ticketProduct) {
      // Sincroniza a quantidade de store_products para ser rigorosamente igual a max_players
      const { data: sp } = await supabase
        .from('store_products')
        .upsert({
          store_id: resolvedStoreId,
          product_id: ticketProduct.id,
          quantity: newSlots,
          store_price: ticketProduct.msrp || 0.05,
          pre_sale: false,
          deleted_at: null
        }, { onConflict: 'store_id,product_id' })
        .select()
        .single();
      stockData = sp;
    }

    return {
      success: true,
      new_max_players: newSlots,
      tournament: updatedTourney,
      stock: stockData
    };
  } catch (error: any) {
    console.error('Error in addTournamentSlot:', error);
    return { error: error?.message || 'Erro ao adicionar vaga ao torneio.' };
  }
};

/**
 * Atribui um usuário do sistema a uma vaga/inscrição de participante de torneio
 * (útil para vagas de ingressos geradas sem usuário ou compras no balcão/guest).
 */
export const assignTournamentParticipant = async (params: {
  tournamentId: number | string;
  entryId: number | string;
  userId: number | string;
  storeId?: number | string;
}) => {
  try {
    const { tournamentId, entryId, userId, storeId } = params;

    // 1. Tenta rota backend com privilégios de serviço (se disponível no ambiente)
    try {
      const response = await fetch('/api/tournaments/assign-participant', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          tournament_id: Number(tournamentId),
          entry_id: Number(entryId),
          user_id: Number(userId),
          store_id: storeId ? Number(storeId) : undefined
        })
      });

      // Leitura resiliente do corpo da resposta (evita SyntaxError no JSON.parse quando corpo for vazio ou HTML)
      const rawText = await response.text();
      let data: any = null;
      if (rawText && rawText.trim().length > 0) {
        try {
          data = JSON.parse(rawText);
        } catch {
          data = null;
        }
      }

      if (response.ok && data && data.success) {
        return data;
      }

      // Se a API retornou erro 400 com mensagem explícita de validação de negócio, repassa a mensagem
      if (response.status === 400 && data?.error) {
        throw new Error(data.error);
      }

      console.warn('API /api/tournaments/assign-participant não retornou sucesso (status ' + response.status + '), prosseguindo com fallback direto Supabase.');
    } catch (apiErr: any) {
      // Se for validação de regra de negócio disparada acima, propaga diretamente ao usuário
      if (apiErr.message && (
        apiErr.message.includes('já está inscrito') ||
        apiErr.message.includes('Não é possível alterar') ||
        apiErr.message.includes('não encontrada') ||
        apiErr.message.includes('não pertence') ||
        apiErr.message.includes('obrigatórios')
      )) {
        throw apiErr;
      }
      console.warn('API /api/tournaments/assign-participant indisponível ou falhou, usando fallback direto Supabase:', apiErr);
    }

    // 2. Fallback direto Supabase (caso a rota /api não esteja disponível em produção ou retorne erro)
    const { data: tourneyData } = await supabase
      .from('tournaments')
      .select('status')
      .eq('id', Number(tournamentId))
      .maybeSingle();

    if (tourneyData && tourneyData.status !== 'open' && tourneyData.status !== 'scheduled') {
      throw new Error('Não é possível alterar participantes após o início do torneio.');
    }

    // Verifica se usuário já está no torneio
    const { data: existing } = await supabase
      .from('tournament_entries')
      .select('id')
      .eq('tournament_id', Number(tournamentId))
      .eq('user_id', Number(userId))
      .neq('id', Number(entryId))
      .maybeSingle();

    if (existing) {
      throw new Error('Este usuário já está inscrito neste torneio.');
    }

    // Atualiza o registro
    const { data: updatedEntry, error: updateErr } = await supabase
      .from('tournament_entries')
      .update({
        user_id: Number(userId),
        updated_at: new Date().toISOString()
      })
      .eq('id', Number(entryId))
      .select()
      .single();

    if (updateErr) {
      throw updateErr;
    }

    // Busca dados seguros do usuário via get_safe_users para retorno imediato
    let userProfile = null;
    try {
      const { data: safeUserData } = await (supabase.rpc('get_safe_users') as any)
        .select('id, username, fullname, avatar')
        .eq('id', Number(userId))
        .maybeSingle();
      userProfile = safeUserData;
    } catch (safeErr) {
      console.warn('Erro ao buscar dados do usuário via get_safe_users:', safeErr);
    }

    if (!userProfile) {
      const { data: directUser } = await supabase
        .from('users')
        .select('id, username, fullname, avatar')
        .eq('id', Number(userId))
        .maybeSingle();
      userProfile = directUser;
    }

    return {
      success: true,
      entry: {
        ...updatedEntry,
        user: userProfile
      },
      user: userProfile
    };
  } catch (error: any) {
    console.error('Error in assignTournamentParticipant:', error);
    return { error: error?.message || 'Falha ao atribuir usuário ao participante.' };
  }
};

/**
 * Verifica se um usuário comprou ingresso pago para um torneio usando
 * a função do banco de dados `public.has_paid_tournament_ticket(user_id, tournament_id)`.
 */
export const checkUserPaidTournamentTicket = async (
  userId: number | string,
  tournamentId: number | string
): Promise<boolean> => {
  try {
    const uid = Number(userId);
    const tid = Number(tournamentId);
    if (!uid || !tid || isNaN(uid) || isNaN(tid)) return false;

    // Tentativa 1: parâmetros nomeados { user_id, tournament_id }
    const { data: data1, error: err1 } = await (supabase.rpc as any)('has_paid_tournament_ticket', {
      user_id: uid,
      tournament_id: tid
    });
    if (!err1 && typeof data1 === 'boolean') {
      return data1;
    }

    // Tentativa 2: parâmetros prefixados { p_user_id, p_tournament_id }
    const { data: data2, error: err2 } = await (supabase.rpc as any)('has_paid_tournament_ticket', {
      p_user_id: uid,
      p_tournament_id: tid
    });
    if (!err2 && typeof data2 === 'boolean') {
      return data2;
    }

    // Tentativa 3: parâmetros sublinhados { _user_id, _tournament_id }
    const { data: data3, error: err3 } = await (supabase.rpc as any)('has_paid_tournament_ticket', {
      _user_id: uid,
      _tournament_id: tid
    });
    if (!err3 && typeof data3 === 'boolean') {
      return data3;
    }

    if (err1 && err2 && err3) {
      console.warn('has_paid_tournament_ticket RPC call error:', err1.message || err2.message || err3.message);
    }

    return false;
  } catch (error) {
    console.error('Error checking paid tournament ticket:', error);
    return false;
  }
};
